import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useGroupComplaints } from "../../lib/api";
import { filterComplaints } from "../../lib/complaints";
import { Icon } from "../msd/Icon";
import { Seg2, Tag, CARD, LBL, cx } from "../msd/ui";
import ComplaintCard from "./ComplaintCard";
import PendingCandidateCard from "./PendingCandidateCard";

const FILTERS = [
  ["open", "Open"],
  ["closed", "Closed"],
  ["all", "All"],
];

// `embedded`: inside another container (Service Desk details) — no card
// chrome or heading, just the filter and the list.
export default function ComplaintsPanel({ row, embedded = false }) {
  const { data, isLoading, error } = useGroupComplaints(row);
  const [filter, setFilter] = useState("open");

  const complaints = useMemo(() => data?.rows || [], [data]);
  const candidates = useMemo(() => data?.candidates || [], [data]);
  const visible = useMemo(() => filterComplaints(complaints, filter), [complaints, filter]);
  // Pending candidates aren't complaints yet, so they only belong under
  // Open/All — never under Closed.
  const visibleCandidates = filter === "closed" ? [] : candidates;
  const config = data?.config;

  // A brand with complaint tracking switched off, in a group that never had a
  // complaint or a pending one, gets no panel rather than a permanently empty one.
  if (!isLoading && !error && config && !config.enabled && complaints.length === 0 && candidates.length === 0) {
    return null;
  }

  const openCount = filterComplaints(complaints, "open").length + candidates.length;
  const emptyText =
    complaints.length === 0 && candidates.length === 0
      ? "No complaints detected in this conversation."
      : filter === "closed"
        ? "No closed complaints."
        : "No open complaints.";

  return (
    <div className={embedded ? "" : cx(CARD, "p-[22px] text-msd-ink")} data-testid="complaints-panel">
      <div className={cx("mb-3 flex flex-wrap items-start gap-3", embedded ? "justify-end" : "justify-between")}>
        <div className={embedded ? "hidden" : ""}>
          <div className="flex items-center gap-2">
            <span className="flex text-msd-faint">
              <Icon name="warn" size={17} stroke={2} />
            </span>
            <h3 className="m-0 text-[16.5px] font-bold text-msd-ink">Complaints</h3>
            {openCount > 0 && (
              <Tag tone="red" data-testid="complaints-open-count">
                {openCount} open
              </Tag>
            )}
          </div>
          <p className={`m-0 mt-1 ${LBL}`}>
            Detected from the customer's messages in this group. Each complaint is tracked on its own.
          </p>
        </div>
        <Seg2 options={FILTERS} value={filter} onChange={setFilter} label="Filter complaints" testid="complaints-filter" />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-2 text-[14px] text-msd-muted">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      ) : error ? (
        <p className="m-0 py-2 text-[13px] text-msd-brick-ink">Failed to load complaints.</p>
      ) : !visible.length && !visibleCandidates.length ? (
        <p
          className="m-0 rounded-[14px] border border-dashed border-msd-line2 p-[22px] text-center text-[14px] text-msd-muted"
          data-testid="complaints-empty"
        >
          {emptyText}
        </p>
      ) : (
        <div className="flex flex-col gap-[10px]" data-testid="complaints-list">
          {visibleCandidates.map((c) => (
            <PendingCandidateCard key={`candidate-${c.id}`} candidate={c} />
          ))}
          {visible.map((c) => (
            <ComplaintCard key={c.id} row={row} complaint={c} config={config} canManage={!!data?.can_manage} />
          ))}
        </div>
      )}
    </div>
  );
}
